<template>
  <div :class="['notification', notificationClass]">
    <div class="notification__content">
      <static-image
        v-if="isPromoMessage && !promoImageLink"
        src="/img/empty-message.webp"
        class="notification__promo-image"
      />
      <div v-else-if="isPromoMessage && promoImageLink" class="notification__promo-image">
        <u-i-compressed-image :compressed-images="promoImageLink" />
      </div>
      <div v-else-if="messageIcon.src || iconName || messageIcon.icon || avatar" class="notification__avatar">
        <img v-if="messageIcon.src" class="notification__custom-icon" :src="messageIcon.src" />
        <icon v-else-if="iconName" :name="iconName" class="notification__icon" />
        <system-message-icon v-else-if="messageIcon.icon" :icon="messageIcon.icon" />
        <img v-else :src="avatar" :alt="'notification_' + type" />
      </div>
      <p v-if="!showDateTime" class="notification__message text-header-md">{{ message }}</p>
      <div v-else class="notification__head">
        <p class="notification__date">{{ showDateTime }}</p>
        <ol-safe-html class="notification__title ol-font" :html="message" />
      </div>
    </div>
    <div v-if="caption" class="notification__caption">
      <ol-safe-html tag="span" :html="caption" />
    </div>
    <ol-button icon-only class="message-close-btn" type="button" size="xs" variant="tertiary" @click="close">
      <template #leftIcon>
        <icon name="close" />
      </template>
    </ol-button>

    <div v-if="actions.length" class="notification-actions">
      <ol-button
        v-for="(action, index) in actions"
        :key="index"
        :class="action.class"
        full-width
        :label="action.label"
        :variant="action.variant || 'primary'"
        @click="handleAction(action)"
      >
        <template v-if="action.icon" #leftIcon>
          <icon v-if="action.icon" :name="action.icon" />
        </template>
      </ol-button>
    </div>
  </div>
</template>

<script setup>
import { onMounted, computed } from 'vue'
import { OlButton, OlSafeHtml } from '@gamesite/game-core'
import SystemMessageIcon from 'components/messages/SystemMessageIcon.vue'
import Icon from 'components/ui/Icon.vue'
import StaticImage from 'components/ui/StaticImage.vue'
import UICompressedImage from 'components/ui/UICompressedImage.vue'
import { useImages } from 'hooks/useImages.js'
import { useMessageImages } from 'hooks/useMessageImages.js'

const props = defineProps({
  message: {
    type: String,
    default: '',
  },
  avatar: {
    type: String,
    default: '',
  },

  dateTime: {
    type: String,
    default: null,
  },
  type: {
    type: String,
    default: '', // info, success, warning, error
  },
  timeout: {
    type: Number,
    default: 3000,
  },
  iconName: {
    type: String,
    default: '',
  },
  systemIcon: {
    type: String,
    default: '',
  },
  caption: {
    type: String,
    default: '',
  },
  customIcon: {
    type: String,
    default: '',
  },
  color: {
    type: String,
    default: '',
  },
  canClose: {
    type: Boolean,
    default: true,
  },
  actions: {
    type: Array,
    default: () => [],
  },
  propsContent: {
    type: Object,
    default: () => ({}),
  },
})

const emit = defineEmits(['close'])

const { getUrlWithModifier } = useImages()
const { getMessageImage, getSystemIconProps } = useMessageImages()

const notificationClass = computed(() => {
  return props.color ? `bg-${props.color}` : props.type
})

const isPromoMessage = computed(() => props.type === 'promo')

const promoImageLink = computed(() => {
  return getMessageImage(props.propsContent)
})

const messageIcon = computed(() => {
  if (props.propsContent?.props) {
    return getSystemIconProps({ propsContent: props.propsContent, icon: props.customIcon })
  }

  return {
    icon: props.systemIcon,
    src: props.customIcon ? getUrlWithModifier(props.customIcon) : null,
  }
})

function close() {
  emit('close')
}

const showDateTime = computed(() => {
  if (props.dateTime) {
    return props.dateTime
  }
  return new Date().toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
})

async function handleAction(action) {
  await action.handler()
  close()
}

onMounted(() => {
  if (props.timeout > 0) {
    setTimeout(close, props.timeout)
  }
})
</script>

<style lang="scss">
.notification {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  min-width: 280px;
  max-width: 95%;
  width: 100%;
  padding: 12px;
  font-size: 16px;
  font-weight: 500;
  border-radius: var(--border-radius-md);
  background: var(--color-background-page-lvl-2);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.5);
  color: var(--color-text-primary);
  pointer-events: all;
  margin: 10px 10px 0;
  position: relative;
  transition:
    transform 1s,
    opacity 0.5s;
  z-index: 5500;
  flex-shrink: 0;
  overflow: hidden;

  .message-close-btn {
    position: absolute;
    display: block;
    right: 16px;
    top: 16px;
    left: auto;
  }

  @media (min-width: 576px) {
    min-width: 330px;
    max-width: 480px;
  }
  &__content {
    display: flex;
    gap: 12px;
    align-items: center;
    overflow: hidden;
  }
  &__promo-image {
    width: 96px;
    height: 64px;
    flex: 0 0 auto;
    border-radius: 8px;
    overflow: hidden;
    align-items: center;
  }
  &__avatar {
    border-radius: 0;
    margin-right: 16px;
    width: 48px;
    height: 48px;
    flex: 0 0 auto;
  }

  &__custom-icon {
    width: 48px;
    height: 48px;
    display: block;
  }

  &__caption {
    width: 100%;
    font-weight: 400;
    font-size: 14px;
    line-height: 18px;
    color: var(--color-text-secondary);
    margin-top: 8px;

    span {
      overflow: hidden;
      line-clamp: 4;
      -webkit-line-clamp: 4;
      -webkit-box-orient: vertical;
      text-overflow: ellipsis;
      word-break: break-word;
    }
  }
  &__message {
    font-size: 18px;
    line-height: 24px;
    font-weight: 500;
    min-height: 20px;
    display: -webkit-box;
    line-clamp: 4;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__head {
    display: flex;
    flex-direction: column;
    gap: 2px;
    overflow: hidden;
  }

  &__date {
    color: var(--color-text-secondary);
    font-size: 12px;
    line-height: 16px;
  }

  &__title {
    color: var(--cas-text-primary);
    font-size: 18px;
    font-weight: 500;
    line-height: 20px;
    letter-spacing: 0.54px;
    overflow: hidden;
    display: -webkit-box;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
  }

  &__icon {
    flex: 0 0 1em;
    margin-right: 16px;
    margin-top: 10px;
  }

  &-actions {
    margin-top: 12px;
    display: flex;
    gap: 8px;
    width: 100%;
    flex-direction: row-reverse;
  }

  &.bg-lootbox {
    .notification__avatar {
      padding: var(--padding-sm);
      border-radius: var(--border-radius-md);
      background: linear-gradient(
        135deg,
        color-mix(in srgb, var(--cas-warning-900) 64%, transparent) 0%,
        color-mix(in srgb, var(--cas-warning-900) 8%, transparent) 100%
      );
    }

    .notification__icon {
      flex: 0 0 var(--size-icon-md);
      width: var(--size-icon-md);
      height: var(--size-icon-md);
      margin: 0;
    }
  }

  &.achievement {
    .notification__avatar {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 0;
      padding: 12px;
      border-radius: 8px;
      background: linear-gradient(135deg, rgba(230, 96, 131, 0.64) 0%, rgba(230, 96, 131, 0.08) 100%);
    }

    .notification__icon {
      flex: 0 0 24px;
      width: 24px;
      height: 24px;
      margin: 0;
    }

    .notification__date {
      display: none;
    }

    .notification__head {
      padding-right: 24px;
    }

    .notification__title {
      display: block;
      overflow: visible;
      line-clamp: unset;
      -webkit-line-clamp: unset;
      text-overflow: clip;
      white-space: pre-line;
      word-break: break-word;
    }

    .message-close-btn {
      right: 12px;
      top: 12px;
    }

    .notification-actions {
      margin-top: 8px;
    }
  }
}
</style>
